import type { DebtCategoryData, DebtMovementData } from '../api/contract';
import { rublesToMinor } from '../lib/money';
export { parseOperationCivilDate as debtCivilDateToInstant } from '../shared/ui/OperationDateField';

export type DebtDragPayload = { kind: 'budget'; budgetId: string } | { kind: 'debt'; categoryId: string; direction: DebtCategoryData['direction'] };
export type DebtActionKind = Exclude<DebtMovementData['kind'], 'LEGACY_BASELINE'>;

export function resolveDebtDrop(source: DebtDragPayload, targetId: string): { categoryId: string; budgetId: string; kind: 'INCREASE' | 'REPAYMENT' } | undefined {
  if (source.kind === 'budget') {
    const match = /^debt:(LENT|BORROWED):(.+)$/u.exec(targetId);
    if (match) return { categoryId: match[2], budgetId: source.budgetId, kind: match[1] === 'LENT' ? 'INCREASE' : 'REPAYMENT' };
  } else if (targetId.startsWith('budget:')) {
    return { categoryId: source.categoryId, budgetId: targetId.slice(7), kind: source.direction === 'LENT' ? 'REPAYMENT' : 'INCREASE' };
  }
  return undefined;
}

export function minorToMoneyInput(amountMinor: number): string {
  const value = BigInt(amountMinor);
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? '-' : ''}${absolute / 100n},${String(absolute % 100n).padStart(2, '0')}`;
}

export function formatDebtMoney(amountMinor: number | bigint): string {
  const value = BigInt(amountMinor);
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? '-' : ''}${absolute / 100n},${String(absolute % 100n).padStart(2, '0')} ₽`;
}

export function debtAction(direction: DebtCategoryData['direction'], kind: DebtActionKind, outstanding = 0): string {
  if (kind === 'OPENING') return 'Указать существующий долг';
  if (kind === 'WRITE_OFF') return direction === 'LENT' ? 'Простить долг' : 'Долг простили';
  if (kind === 'INCREASE') return direction === 'LENT' ? outstanding === 0 ? 'Дать в долг' : 'Дать ещё' : outstanding === 0 ? 'Взять в долг' : 'Взять ещё';
  return direction === 'LENT' ? 'Получить возврат' : 'Вернуть долг';
}

export function debtMovementLabel(direction: DebtCategoryData['direction'], kind: DebtMovementData['kind']): string {
  if (kind === 'LEGACY_BASELINE') return 'Служебная запись переноса долга';
  if (kind === 'OPENING') return 'Начальный долг';
  if (kind === 'WRITE_OFF') return debtAction(direction, kind);
  return debtAction(direction, kind);
}

export function debtPreview(balance: number, amount: number, kind: DebtActionKind): string {
  const decreases = kind === 'REPAYMENT' || kind === 'WRITE_OFF';
  const next = decreases ? balance - amount : balance + amount;
  if (next === 0 && decreases) return kind === 'WRITE_OFF' ? 'Долг будет закрыт без перевода денег' : 'Долг будет погашен полностью';
  return `Останется ${formatDebtMoney(next)}`;
}

export function debtEffectPreview(kind: DebtActionKind, balance: number, amount?: number): { title: string; body?: string } | undefined {
  const cash = kind === 'INCREASE' || kind === 'REPAYMENT';
  const valid = amount !== undefined && Number.isSafeInteger(amount) && amount > 0 && amount <= 9_000_000_000_000;
  if (cash && !valid) return undefined;
  const remaining = BigInt(balance) + BigInt(valid ? amount! : 0) * (kind === 'REPAYMENT' || kind === 'WRITE_OFF' ? -1n : 1n);
  const unchanged = 'Бюджеты не изменятся';
  if (valid && (remaining < BigInt(Number.MIN_SAFE_INTEGER) || remaining > BigInt(Number.MAX_SAFE_INTEGER))) return { title: 'Результат вне допустимого диапазона', ...(!cash ? { body: unchanged } : {}) };
  if (kind === 'WRITE_OFF') {
    if (!valid) return { title: 'Прощение долга', body: `Уменьшает долг без перевода денег. ${unchanged}` };
    if (remaining < 0n) return { title: 'Сумма превышает остаток', body: unchanged };
    if (remaining === 0n) return { title: 'Долг будет закрыт без перевода денег', body: unchanged };
    return { title: 'Часть долга будет прощена', body: `После сохранения останется: ${formatDebtMoney(remaining)}. ${unchanged}` };
  }
  if (kind === 'OPENING') return valid
    ? { title: 'Будет учтён существующий долг', body: `После сохранения остаток: ${formatDebtMoney(remaining)}. ${unchanged}` }
    : { title: 'Существующий долг', body: `Укажите долг, который уже есть. ${unchanged}` };
  return { title: remaining === 0n ? 'Долг будет погашен полностью' : `После действия остаток: ${formatDebtMoney(remaining)}` };
}

export function validateDebtInput(amount: string, date: string, note: string, kind: DebtActionKind, balance: number, originalNote?: string | null) {
  const amountMinor = rublesToMinor(amount);
  if (amountMinor > 9_000_000_000_000) throw new Error('Сумма слишком велика');
  if ((kind === 'REPAYMENT' || kind === 'WRITE_OFF') && amountMinor > balance) throw new Error('Сумма превышает текущий остаток');
  const parsed = new Date(date);
  if (!Number.isFinite(parsed.getTime()) || parsed.getTime() > Date.now()) throw new Error('Выберите корректную дату не позднее текущего момента');
  if (note !== originalNote && note.length > 120) throw new Error('Комментарий — до 120 символов');
  if (kind === 'WRITE_OFF' && !note.trim()) throw new Error('Укажите причину прощения');
  return { amountMinor, occurredAt: parsed.toISOString(), note: note === originalNote ? note : note.trim() };
}
